import React, { useState, useEffect, useMemo } from 'react';
import { TechnicalIndicators } from '../utils/technicalIndicators';
import { TradingML } from '../utils/machineLearning';
import { SentimentAnalyzer } from '../utils/sentimentAnalysis';
import './AITradingPanel.css';

const AITradingPanel = ({ marketData, trades, onExecuteTrade }) => {
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [sentiment, setSentiment] = useState(null);
  const [riskAssessment, setRiskAssessment] = useState(null);
  const [portfolioOptimization, setPortfolioOptimization] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const ml = useMemo(() => new TradingML(), []);
  const sentimentAnalyzer = useMemo(() => new SentimentAnalyzer(), []);

  // Perform comprehensive AI analysis
  const performAIAnalysis = async () => {
    setIsAnalyzing(true);
    
    try {
      const analysis = {
        technical: null,
        sentiment: null,
        risk: null,
        portfolio: null,
        suggestions: []
      };

      // Technical Analysis
      if (marketData && marketData.prices && marketData.prices.length > 0) {
        analysis.technical = TechnicalIndicators.analyze(marketData.prices);
      }

      // Sentiment Analysis
      analysis.sentiment = await sentimentAnalyzer.getMarketSentiment();

      // Risk Assessment
      if (trades && trades.length > 0) {
        const activeTrades = trades.filter(t => t.status === 'ACTIVE');
        if (activeTrades.length > 0) {
          const latestTrade = activeTrades[0];
          analysis.risk = ml.assessRisk(latestTrade, marketData, {
            trades: activeTrades,
            totalValue: 10000 // Mock portfolio value
          });
        }
      }

      // Portfolio Optimization
      if (trades && trades.length > 0) {
        analysis.portfolio = ml.optimizePortfolio(trades, 10000, 'MEDIUM');
      }

      // Generate AI Suggestions
      if (marketData) {
        analysis.suggestions = ml.generateAISuggestions(marketData, {
          trades: trades || [],
          totalValue: 10000
        });
      }

      setAiAnalysis(analysis);
      setSentiment(analysis.sentiment);
      setRiskAssessment(analysis.risk);
      setPortfolioOptimization(analysis.portfolio);

    } catch (error) {
      console.error('Error performing AI analysis:', error);
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Auto-refresh analysis every 30 seconds
  useEffect(() => {
    performAIAnalysis();
    const interval = setInterval(performAIAnalysis, 30000);
    return () => clearInterval(interval);
  }, [marketData, trades]);

  const executeAITrade = (suggestion) => {
    const trade = {
      id: Date.now().toString(),
      pair: suggestion.pair,
      action: suggestion.action,
      amount: 100, // Default amount
      leverage: 10, // Default leverage
      entryPrice: suggestion.entryPrice,
      currentPrice: suggestion.entryPrice,
      timestamp: new Date().toISOString(),
      type: 'AI_TRADING',
      status: 'ACTIVE',
      profitLoss: 0,
      profitPercent: 0,
      aiData: {
        confidence: suggestion.confidence,
        reason: suggestion.reason,
        model: suggestion.type
      }
    };

    onExecuteTrade(trade);
  };

  const getSentimentColor = (score) => {
    if (score > 20) return '#4caf50';
    if (score > -20) return '#ff9800';
    return '#f44336';
  };

  const getSentimentIcon = (level) => {
    switch (level) {
      case 'VERY_BULLISH': return '🚀';
      case 'BULLISH': return '📈';
      case 'NEUTRAL': return '➡️';
      case 'BEARISH': return '📉';
      case 'VERY_BEARISH': return '💥';
      default: return '❓';
    }
  };

  return (
    <div className="ai-trading-panel">
      <div className="ai-header">
        <h3>🤖 AI Trading Analysis</h3>
        <button 
          className={`analyze-btn ${isAnalyzing ? 'analyzing' : ''}`}
          onClick={performAIAnalysis}
          disabled={isAnalyzing}
        >
          {isAnalyzing ? 'Analyzing...' : 'Refresh Analysis'}
        </button>
      </div>

      {isAnalyzing && (
        <div className="ai-loading">
          <div className="loading-spinner"></div>
          <p>AI is analyzing market conditions...</p>
        </div>
      )}

      {aiAnalysis && (
        <div className="ai-content">
          {/* Technical Analysis */}
          {aiAnalysis.technical && (
            <div className="analysis-section">
              <h4>📊 Technical Analysis</h4>
              <div className="technical-grid">
                <div className="tech-indicator">
                  <span className="label">RSI:</span>
                  <span className={`value ${aiAnalysis.technical.rsi > 70 ? 'overbought' : aiAnalysis.technical.rsi < 30 ? 'oversold' : 'normal'}`}>
                    {aiAnalysis.technical.rsi?.toFixed(1) || 'N/A'}
                  </span>
                </div>
                <div className="tech-indicator">
                  <span className="label">MACD:</span>
                  <span className={`value ${aiAnalysis.technical.macd?.macd > aiAnalysis.technical.macd?.signal ? 'bullish' : 'bearish'}`}>
                    {aiAnalysis.technical.macd?.macd?.toFixed(2) || 'N/A'}
                  </span>
                </div>
                <div className="tech-indicator">
                  <span className="label">SMA 20:</span>
                  <span className="value">{aiAnalysis.technical.sma20?.toFixed(2) || 'N/A'}</span>
                </div>
                <div className="tech-indicator">
                  <span className="label">Trend:</span>
                  <span className={`value ${aiAnalysis.technical.signals?.trend?.toLowerCase()}`}>
                    {aiAnalysis.technical.signals?.trend || 'N/A'}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Sentiment Analysis */}
          {sentiment && (
            <div className="analysis-section">
              <h4>💭 Market Sentiment</h4>
              <div className="sentiment-display">
                <div className="sentiment-overview">
                  <div className="sentiment-score">
                    <span className="score-label">Overall Score:</span>
                    <span 
                      className="score-value"
                      style={{ color: getSentimentColor(sentiment.overall.score) }}
                    >
                      {sentiment.overall.score.toFixed(1)}
                    </span>
                    <span className="sentiment-level">
                      {getSentimentIcon(sentiment.overall.level)} {sentiment.overall.level}
                    </span>
                  </div>
                  <div className="sentiment-confidence">
                    <span className="confidence-label">Confidence:</span>
                    <span className="confidence-value">
                      {(sentiment.overall.confidence * 100).toFixed(0)}%
                    </span>
                  </div>
                </div>
                <div className="sentiment-breakdown">
                  <div className="sentiment-source">
                    <span className="source-label">News:</span>
                    <span className="source-score">{sentiment.news.score.toFixed(1)}</span>
                    <span className="source-count">({sentiment.news.count} articles)</span>
                  </div>
                  <div className="sentiment-source">
                    <span className="source-label">Social:</span>
                    <span className="source-score">{sentiment.social.score.toFixed(1)}</span>
                    <span className="source-count">({sentiment.social.count} posts)</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Risk Assessment */}
          {riskAssessment && (
            <div className="analysis-section">
              <h4>⚠️ Risk Assessment</h4>
              <div className="risk-display">
                <div className="risk-score">
                  <span className="risk-label">Risk Score:</span>
                  <span className={`risk-value ${riskAssessment.level.toLowerCase()}`}>
                    {riskAssessment.score}/100 ({riskAssessment.level})
                  </span>
                </div>
                <div className="risk-factors">
                  <div className="risk-factor">
                    <span>Volatility:</span>
                    <span>{(riskAssessment.factors.volatility * 100).toFixed(1)}%</span>
                  </div>
                  <div className="risk-factor">
                    <span>Leverage:</span>
                    <span>{riskAssessment.factors.leverage}x</span>
                  </div>
                  <div className="risk-factor">
                    <span>Portfolio Exposure:</span>
                    <span>{(riskAssessment.factors.portfolioExposure * 100).toFixed(1)}%</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Portfolio Optimization */}
          {portfolioOptimization && (
            <div className="analysis-section">
              <h4>📈 Portfolio Optimization</h4>
              <div className="portfolio-display">
                <div className="portfolio-metrics">
                  <div className="metric">
                    <span className="metric-label">Diversification:</span>
                    <span className="metric-value">{portfolioOptimization.diversification}%</span>
                  </div>
                  <div className="metric">
                    <span className="metric-label">Risk Level:</span>
                    <span className="metric-value">{portfolioOptimization.riskLevel}</span>
                  </div>
                </div>
                {Object.keys(portfolioOptimization.recommendedAllocation).length > 0 && (
                  <div className="allocation-recommendations">
                    <h5>Allocation Recommendations:</h5>
                    {Object.entries(portfolioOptimization.recommendedAllocation).map(([pair, rec]) => (
                      <div key={pair} className="allocation-rec">
                        <span className="pair">{pair}:</span>
                        <span className="current">{rec.current.toFixed(1)}%</span>
                        <span className="arrow">→</span>
                        <span className="recommended">{rec.recommended}%</span>
                        <span className={`action ${rec.action.toLowerCase()}`}>{rec.action}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* AI Trading Suggestions */}
          {aiAnalysis.suggestions && aiAnalysis.suggestions.length > 0 && (
            <div className="analysis-section">
              <h4>🎯 AI Trading Suggestions</h4>
              <div className="suggestions-list">
                {aiAnalysis.suggestions.map((suggestion, index) => (
                  <div key={index} className="suggestion-card">
                    <div className="suggestion-header">
                      <span className="suggestion-type">{suggestion.type}</span>
                      <span className="suggestion-confidence">
                        {(suggestion.confidence * 100).toFixed(0)}% confidence
                      </span>
                    </div>
                    <div className="suggestion-content">
                      <div className="suggestion-pair">{suggestion.pair}</div>
                      <div className="suggestion-action">
                        <span className={`action-badge ${suggestion.action.toLowerCase()}`}>
                          {suggestion.action}
                        </span>
                        <span className="suggestion-price">${suggestion.entryPrice?.toFixed(2)}</span>
                      </div>
                      <div className="suggestion-reason">{suggestion.reason}</div>
                    </div>
                    <div className="suggestion-actions">
                      <button 
                        className="execute-btn"
                        onClick={() => executeAITrade(suggestion)}
                      >
                        Execute Trade
                      </button>
                      <button className="ignore-btn">Ignore</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AITradingPanel;

